/**
 * ToolsScreen — 5th tab (replaces the old standalone Governance tab).
 *
 * A hub of on-chain admin actions: create an account, create a token,
 * governance voting, key/permission management, multisig, plus quick links
 * into the Blocksfinity explorer for things we don't reimplement ourselves
 * (name bids, top holders, account alerts).
 */

import { useState } from 'react'
import { Capacitor } from '@capacitor/core'
import {
  BLOCKSFINITY_URL,
  BLOCKSFINITY_NAMEBIDS_URL,
  BLOCKSFINITY_TOPHOLDERS_URL,
  BLOCKSFINITY_ALERTS_URL,
} from '@properwallet/chain-eos'
import { useWalletStore } from '../store/wallet.store'
import { useTranslation } from '../hooks/useTranslation'
import BottomNav from '../components/BottomNav'
import AccountAvatar from '../components/AccountAvatar'
import DAppWebView from '../components/DAppWebView'
import type { WalletScreen } from '../store/wallet.store'

interface ToolCardProps {
  icon: JSX.Element
  title: string
  desc: string
  onClick: () => void
}

function ToolCard({ icon, title, desc, onClick }: ToolCardProps) {
  return (
    <button type="button" onClick={onClick} className="card w-full text-left flex items-center gap-3">
      <div
        className="flex-shrink-0 flex items-center justify-center rounded-xl"
        style={{ width: 40, height: 40, background: 'rgba(67,160,71,0.12)', color: '#5cbf63' }}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-semibold text-sm">{title}</p>
        <p className="text-gray-500 text-xs mt-0.5 truncate">{desc}</p>
      </div>
      <span className="text-gray-500 text-lg flex-shrink-0">›</span>
    </button>
  )
}

function ExplorerLinkRow({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-between py-2.5 px-1 text-sm text-gray-300"
      style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
    >
      <span>{label}</span>
      <span className="text-gray-500">↗</span>
    </button>
  )
}

const IconResources = (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="16" height="10" rx="2" /><path d="M22 10v4" /><path d="M6 11v2" />
  </svg>
)
const IconAccount = (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" />
  </svg>
)
const IconToken = (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" /><path d="M12 7v10M9 9.5c0-1.4 1.3-2.5 3-2.5s3 1 3 2.2c0 2.8-6 1.3-6 4 0 1.3 1.3 2.3 3 2.3s3-1 3-2.3" />
  </svg>
)
const IconGovernance = (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 22h18" /><path d="M6 18V11" /><path d="M10 18V11" /><path d="M14 18V11" /><path d="M18 18V11" /><path d="M12 2L2 7h20L12 2z" />
  </svg>
)
const IconKeys = (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="8" cy="15" r="4" /><path d="M10.5 12.5 20 3M17 6l3 3M14 9l2 2" />
  </svg>
)
const IconMultisig = (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </svg>
)

export default function ToolsScreen() {
  const { t } = useTranslation()
  const { activeAccount, navigate } = useWalletStore()
  const [openUrl, setOpenUrl] = useState<string | null>(null)

  // Read-only Blocksfinity explorer links don't need the wallet-bridge
  // injection our DAppWebView provides for real dApps. On native we still
  // route them through DAppWebView (it opens a proper separate system
  // WebView there, no downside). On web, DAppWebView's fallback renders an
  // <iframe>, which sites like Blocksfinity legitimately refuse via
  // X-Frame-Options/CSP frame-ancestors (anti-clickjacking) — so on web we
  // just open a normal new tab instead of hitting that wall.
  const openExplorerUrl = (url: string) => {
    if (Capacitor.isNativePlatform()) {
      setOpenUrl(url)
    } else {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }

  const go = (screen: WalletScreen) => navigate(screen)

  if (openUrl) {
    return <DAppWebView url={openUrl} onClose={() => setOpenUrl(null)} />
  }

  return (
    <div className="flex flex-col h-full" style={{ background: '#000000' }}>
      <div
        className="pt-safe px-4 pb-4 flex-shrink-0"
        style={{ background: '#0c100c', borderBottom: '1px solid rgba(255,255,255,0.04)' }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <AccountAvatar avatarUrl={activeAccount?.avatarUrl} size="xs" />
          <div className="min-w-0">
            <h2 className="text-white font-bold" style={{ fontSize: 20 }}>{t('tools.title')}</h2>
            <p className="text-gray-500 text-xs truncate">{t('tools.subtitle')}</p>
          </div>
        </div>
      </div>

      <div className="scroll-area flex-1 px-4 py-4 space-y-2">
        <ToolCard
          icon={IconResources}
          title={t('tools.resourcesCard')}
          desc={t('tools.resourcesDesc')}
          onClick={() => go('resources')}
        />
        <ToolCard
          icon={IconAccount}
          title={t('tools.createAccountCard')}
          desc={t('tools.createAccountDesc')}
          onClick={() => go('create-account')}
        />
        {/* Create Token hidden for the first public release (1.3.5): the
            deploy-on-own-account flow was never live-tested and silently costs
            the user ~180 KB RAM (~62 EOS) for setcode with no warning. Bring it
            back after a Jungle4 run, or with the shared token-factory contract.
            CreateTokenScreen + chain-eos/token-create.ts stay in the codebase. */}
        {false && (
          <ToolCard
            icon={IconToken}
            title={t('tools.createTokenCard')}
            desc={t('tools.createTokenDesc')}
            onClick={() => go('create-token')}
          />
        )}
        <ToolCard
          icon={IconGovernance}
          title={t('tools.governanceCard')}
          desc={t('tools.governanceDesc')}
          onClick={() => go('governance')}
        />
        <ToolCard
          icon={IconKeys}
          title={t('tools.keysCard')}
          desc={t('tools.keysDesc')}
          onClick={() => go('keys')}
        />
        <ToolCard
          icon={IconMultisig}
          title={t('tools.multisigCard')}
          desc={t('tools.multisigDesc')}
          onClick={() => go('multisig')}
        />

        {/* Blocksfinity explorer — read-only links, opened in the in-app browser */}
        <div className="card mt-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-semibold text-white">{t('tools.explorerTitle')}</p>
            <button
              type="button"
              onClick={() => openExplorerUrl(BLOCKSFINITY_URL)}
              className="btn-ghost text-xs"
              style={{ padding: '4px 10px' }}
            >
              {t('tools.explorerOpen')}
            </button>
          </div>
          <div className="mt-2">
            <ExplorerLinkRow label={t('tools.explorerNameBids')} onClick={() => openExplorerUrl(BLOCKSFINITY_NAMEBIDS_URL)} />
            <ExplorerLinkRow label={t('tools.explorerTopHolders')} onClick={() => openExplorerUrl(BLOCKSFINITY_TOPHOLDERS_URL)} />
            <ExplorerLinkRow label={t('tools.explorerAlerts')} onClick={() => openExplorerUrl(BLOCKSFINITY_ALERTS_URL)} />
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  )
}
